import { useState } from "react";
import { useToast } from "components-lib";
import { axiosInstance, API_ENDPOINTS } from "config/api";

/**
 * Custom hook for managing doctor-related operations
 * Handles doctor editing, form state management, and API interactions
 */
export const useDoctorManagement = () => {
  const toast = useToast();

  // Edit Doctor Modal States
  const [isEditDoctorModalOpen, setIsEditDoctorModalOpen] = useState(false);
  const [selectedDoctorForEdit, setSelectedDoctorForEdit] = useState(null);
  const [editDoctorForm, setEditDoctorForm] = useState({
    id: '',
    doctor: '',
    slot_duration: '',
    opd_timing: {
      monday: '',
      tuesday: '',
      wednesday: '',
      thursday: '',
      friday: '',
      saturday: '',
      sunday: ''
    },
    emergency_charge: '',
    payment_details: {
      account_holder: '',
      account_number: '',
      bank_name: '',
      ifsc_code: '',
      upi_id: '',
    },
    language: '',
    whatsapp_no: '',
    medical_license: '',
    qualification: '',
    services: [{ serviceName: '', unitPrice: '', discount: '' }],
    clinic_id: '',
    isParameters: 0,
    speech_to_text: 0
  });
  const [editDoctorLoading, setEditDoctorLoading] = useState(false);
  const [editDoctorErrors, setEditDoctorErrors] = useState({});

  // Edit Doctor Functions
  const handleEditDoctor = async (doctor) => {
    try {
      setEditDoctorLoading(true);
      // Fetch complete doctor data
      const response = await axiosInstance.get(`/api/doctor/profile?doctor_id=${doctor.id}`);

      if (response.data) {
        // The API returns doctor data nested under response.data.doctor
        const doctorData = response.data.doctor || response.data;

        // Handle payment details - support both old and new format
        let paymentDetails = {
          account_holder: '',
          account_number: '',
          bank_name: '',
          ifsc_code: '',
          upi_id: '',
        };

        if (doctorData.payment_details && typeof doctorData.payment_details === 'object') {
          // New structured format
          paymentDetails = {
            account_holder: doctorData.payment_details.account_holder || '',
            account_number: doctorData.payment_details.account_number || '',
            bank_name: doctorData.payment_details.bank_name || '',
            ifsc_code: doctorData.payment_details.ifsc_code || '',
            upi_id: doctorData.payment_details.upi_id || '',
          };
        } else {
          // Old format - convert from upi_details and bank_details strings
          if (doctorData.upi_details) {
            paymentDetails.upi_id = doctorData.upi_details;
          }
          if (doctorData.bank_details) {
            // Keep the old bank_details as a fallback in bank_name field
            paymentDetails.bank_name = doctorData.bank_details;
          }
        }

        // Parse OPD timing if it's a string
        let opdTiming = doctorData.opd_timing;
        if (typeof opdTiming === 'string') {
          try {
            opdTiming = JSON.parse(opdTiming);
          } catch (error) {
            console.error('Error parsing OPD timing:', error);
            opdTiming = {
              monday: '',
              tuesday: '',
              wednesday: '',
              thursday: '',
              friday: '',
              saturday: '',
              sunday: ''
            };
          }
        }

        // Parse services from the API response format
        let services = [{ serviceName: '', unitPrice: '', discount: '' }];
        if (doctorData.service && doctorData.unit_price && doctorData.discount) {
          const serviceNames = doctorData.service.split(',');
          const unitPrices = doctorData.unit_price.split(',');
          const discounts = doctorData.discount.split(',');
          
          services = serviceNames.map((name, index) => ({
            serviceName: name.trim(),
            unitPrice: unitPrices[index]?.trim() || '',
            discount: discounts[index]?.trim() || '0'
          }));
        } else if (doctorData.services && Array.isArray(doctorData.services)) {
          services = doctorData.services;
        }

        console.log('=== DOCTOR DATA FROM API ===');
        console.log('Raw doctor data:', doctorData);
        console.log('isParameters:', doctorData.isParameters, typeof doctorData.isParameters);
        console.log('speech_to_text:', doctorData.speech_to_text, typeof doctorData.speech_to_text);
        console.log('=== END DOCTOR DATA ===');

        // Populate the form with existing data
        setEditDoctorForm({
          id: doctorData.id || doctor.id,
          doctor: doctorData.doctor || '',
          slot_duration: doctorData.slot_duration || '',
          opd_timing: opdTiming || {
            monday: '',
            tuesday: '',
            wednesday: '',
            thursday: '',
            friday: '',
            saturday: '',
            sunday: ''
          },
          emergency_charge: doctorData.emergency_charge || '',
          payment_details: paymentDetails,
          language: doctorData.language || '',
          whatsapp_no: doctorData.whatsapp_no || '',
          medical_license: doctorData.medical_license || '',
          qualification: doctorData.qualification || '',
          services: services,
          clinic_id: doctorData.clinic_id || '',
          isParameters: doctorData.isParameters !== undefined ? Number(doctorData.isParameters) : 0,
          speech_to_text: doctorData.speech_to_text !== undefined ? Number(doctorData.speech_to_text) : 0
        });

        setSelectedDoctorForEdit(doctorData);
        setIsEditDoctorModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching doctor data:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch doctor data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setEditDoctorLoading(false);
    }
  };

  const handleEditDoctorInputChange = (e) => {
    const { name, value } = e.target;
    setEditDoctorForm(prev => ({ ...prev, [name]: value }));
    // Clear error when field is edited
    if (editDoctorErrors[name]) {
      setEditDoctorErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleEditDoctorOpdTimingChange = (day, value) => {
    setEditDoctorForm(prev => ({
      ...prev,
      opd_timing: {
        ...prev.opd_timing,
        [day]: value
      }
    }));
  };

  const handleEditDoctorPaymentChange = (e) => {
    const { name, value } = e.target;
    setEditDoctorForm(prev => ({
      ...prev,
      payment_details: {
        ...prev.payment_details,
        [name]: value
      }
    }));
  };

  const handleEditDoctorServiceChange = (index, field, value) => {
    const updatedServices = editDoctorForm.services.map((service, i) =>
      i === index ? { ...service, [field]: value } : service
    );
    setEditDoctorForm(prev => ({ ...prev, services: updatedServices }));
  };

  const handleEditDoctorToggleChange = (field, value) => {
    setEditDoctorForm(prev => ({ ...prev, [field]: value }));
  };

  const addEditDoctorService = () => {
    setEditDoctorForm(prev => ({
      ...prev,
      services: [...prev.services, { serviceName: '', unitPrice: '', discount: '' }]
    }));
  };

  const removeEditDoctorService = (index) => {
    if (editDoctorForm.services.length > 1) {
      setEditDoctorForm(prev => ({
        ...prev,
        services: prev.services.filter((_, i) => i !== index)
      }));
    }
  };

  const handleEditDoctorSubmit = async (e) => {
    e.preventDefault();
    setEditDoctorLoading(true);
    setEditDoctorErrors({});

    try {
      // Convert structured payment_details back to old format for API
      const { payment_details } = editDoctorForm;
      let upi_details = '';
      let bank_details = '';

      if (payment_details) {
        // Build UPI details string
        if (payment_details.upi_id) {
          upi_details = payment_details.upi_id;
        }

        // Build bank details string
        const bankParts = [];
        if (payment_details.bank_name) bankParts.push(payment_details.bank_name);
        if (payment_details.account_number) bankParts.push(`A/C: ${payment_details.account_number}`);
        if (payment_details.ifsc_code) bankParts.push(`IFSC: ${payment_details.ifsc_code}`);
        if (payment_details.account_holder) bankParts.push(`Holder: ${payment_details.account_holder}`);

        bank_details = bankParts.join(', ');
      }

      // Prepare the data according to the API format
      const updateData = {
        id: editDoctorForm.id,
        doctor: editDoctorForm.doctor,
        slot_duration: editDoctorForm.slot_duration,
        opd_timing: editDoctorForm.opd_timing,
        emergency_charge: editDoctorForm.emergency_charge,
        upi_details: upi_details,
        bank_details: bank_details,
        language: editDoctorForm.language,
        whatsapp_no: editDoctorForm.whatsapp_no,
        medical_license: editDoctorForm.medical_license,
        qualification: editDoctorForm.qualification,
        services: editDoctorForm.services,
        clinic_id: editDoctorForm.clinic_id,
        isParameters: editDoctorForm.isParameters,
        speech_to_text: editDoctorForm.speech_to_text
      };

      console.log('=== API CALL BODY (useDoctorManagement) ===');
      console.log(JSON.stringify(updateData, null, 2));
      console.log('=== END API CALL BODY ===');

      console.log('Updating doctor with data:', updateData);

      const response = await axiosInstance.post(API_ENDPOINTS.DOCTOR.UPDATE, updateData);

      if (response.data) {
        toast({
          title: 'Success',
          description: 'Doctor updated successfully!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Close the modal
        setIsEditDoctorModalOpen(false);

        // Reset form
        setEditDoctorForm({
          id: '',
          doctor: '',
          slot_duration: '',
          opd_timing: {
            monday: '',
            tuesday: '',
            wednesday: '',
            thursday: '',
            friday: '',
            saturday: '',
            sunday: ''
          },
          emergency_charge: '',
          payment_details: {
            account_holder: '',
            account_number: '',
            bank_name: '',
            ifsc_code: '',
            upi_id: '',
          },
          language: '',
          whatsapp_no: '',
          medical_license: '',
          qualification: '',
          services: [{ serviceName: '', unitPrice: '', discount: '' }],
          clinic_id: '',
          isParameters: 0,
          speech_to_text: 0
        });
      }
    } catch (error) {
      console.error('Error updating doctor:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update doctor',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setEditDoctorLoading(false);
    }
  };

  const closeEditDoctorModal = () => {
    setIsEditDoctorModalOpen(false);
    setSelectedDoctorForEdit(null);
    setEditDoctorErrors({});
  };

  return {
    // State
    isEditDoctorModalOpen,
    selectedDoctorForEdit,
    editDoctorForm,
    editDoctorLoading,
    editDoctorErrors,

    // Actions
    handleEditDoctor,
    handleEditDoctorInputChange,
    handleEditDoctorOpdTimingChange,
    handleEditDoctorPaymentChange,
    handleEditDoctorServiceChange,
    handleEditDoctorToggleChange,
    addEditDoctorService,
    removeEditDoctorService,
    handleEditDoctorSubmit,
    closeEditDoctorModal
  };
};